import { ref, computed, reactive } from 'vue'
import api from '@/services/api'
import { DateTime } from 'luxon';

const calculateLevel = (ordem) => {
  if (!ordem) return 0
  const cleanOrder = ordem.replace(/\.$/, '')
  const parts = cleanOrder.split('.')
  return parts.length - 1
}

const calculateNextOrder = (allActivities, parentOrderValue = null) => {
  const activities = Array.isArray(allActivities) ? allActivities : []

  if (activities.length === 0) {
    return parentOrderValue ? `${parentOrderValue}.1` : '1'
  }

  if (!parentOrderValue) {
    const mainActivities = activities.filter(activity => {
      const level = calculateLevel(activity.ordem || activity.order)
      return level === 0
    })

    if (mainActivities.length === 0) {
      return '1'
    }

    const maxNumber = Math.max(...mainActivities.map(activity => {
      const ordem = activity.ordem || activity.order || '0'
      const cleanOrder = ordem.replace(/\.$/, '')
      const parts = cleanOrder.split('.')
      return parseInt(parts[0]) || 0
    }))

    return (maxNumber + 1).toString()
  } else {
    const parentOrder = parentOrderValue.replace(/\.$/, '')

    const directChildren = activities.filter(activity => {
      const ordem = activity.ordem || activity.order
      if (!ordem) return false

      const cleanOrder = ordem.replace(/\.$/, '')
      return cleanOrder.startsWith(parentOrder + '.') &&
        cleanOrder.split('.').length === parentOrder.split('.').length + 1
    })

    if (directChildren.length === 0) {
      return `${parentOrder}.1`
    }

    const maxNumber = Math.max(...directChildren.map(activity => {
      const ordem = activity.ordem || activity.order || '0'
      const cleanOrder = ordem.replace(/\.$/, '')
      const parts = cleanOrder.split('.')
      return parseInt(parts[parts.length - 1]) || 0
    }))

    return `${parentOrder}.${maxNumber + 1}`
  }
}

const buildHierarchyByOrder = (activities) => {
  const activityMap = new Map()
  const rootActivities = []

  activities.forEach(activity => {
    activityMap.set(activity.id, {
      ...activity,
      children: [],
      level: 0
    })
  })

  const isChildOf = (childOrder, parentOrder) => {
    if (!childOrder || !parentOrder) return false
    const cleanChildOrder = childOrder.replace(/\.$/, '')
    const cleanParentOrder = parentOrder.replace(/\.$/, '')
    return cleanChildOrder.startsWith(cleanParentOrder + '.') &&
      cleanChildOrder.split('.').length === cleanParentOrder.split('.').length + 1
  }

  const findDirectParent = (activity, allActivities) => {
    if (!activity.ordem) return null
    const currentLevel = calculateLevel(activity.ordem)
    if (currentLevel === 0) return null

    for (const potentialParent of allActivities) {
      if (potentialParent.id === activity.id) continue
      const parentLevel = calculateLevel(potentialParent.ordem)
      if (parentLevel === currentLevel - 1 && isChildOf(activity.ordem, potentialParent.ordem)) {
        return potentialParent
      }
    }
    return null
  }

  activities.forEach(activity => {
    const currentActivity = activityMap.get(activity.id)
    const level = calculateLevel(activity.ordem)
    currentActivity.level = level

    if (level === 0) {
      rootActivities.push(currentActivity)
    } else {
      const directParent = findDirectParent(activity, activities)
      if (directParent) {
        const parentActivity = activityMap.get(directParent.id)
        if (parentActivity) {
          parentActivity.children.push(currentActivity)
        } else {
          rootActivities.push(currentActivity)
        }
      } else {
        rootActivities.push(currentActivity)
      }
    }
  })

  const sortActivitiesByOrder = (activities) => {
    activities.sort((a, b) => {
      const orderA = a.ordem || '0'
      const orderB = b.ordem || '0'
      const partsA = orderA.replace(/\.$/, '').split('.').map(n => parseInt(n) || 0)
      const partsB = orderB.replace(/\.$/, '').split('.').map(n => parseInt(n) || 0)
      const maxLength = Math.max(partsA.length, partsB.length)

      for (let i = 0; i < maxLength; i++) {
        const partA = partsA[i] || 0
        const partB = partsB[i] || 0
        if (partA !== partB) {
          return partA - partB
        }
      }
      return 0
    })

    activities.forEach(activity => {
      if (activity.children && activity.children.length > 0) {
        sortActivitiesByOrder(activity.children)
      }
    })
  }

  sortActivitiesByOrder(rootActivities)
  return rootActivities
}

const mapApiToActivity = (apiActivity, allActivities = [], statusList = []) => {
  const mapStatusToInternal = (apiStatus) => {
    const statusItem = statusList.find(s => s.status === apiStatus)
    if (statusItem) {
      const mapping = {
        'Não Iniciada': 'not_started',
        'Em Andamento': 'in_progress',
        'Concluída': 'completed'
      }
      return mapping[statusItem.status] || 'not_started'
    }
    return 'not_started'
  }

  const parseResponsaveis = (responsaveisArray) => {
    if (!responsaveisArray || !Array.isArray(responsaveisArray)) return []
    return responsaveisArray.map(responsavel => ({
      id: responsavel.id,
      name: responsavel.nome,
      avatar: null
    }))
  }

  const isOverdue = (dataPrazo, status) => {
    if (!dataPrazo || status === 'Concluída') return false
    const today = new Date()
    const dueDate = new Date(dataPrazo)
    today.setHours(0, 0, 0, 0)
    dueDate.setHours(0, 0, 0, 0)
    return dueDate < today
  }

  const inferPriority = (dataPrazo) => {
    if (!dataPrazo) return 'medium'
    const today = new Date()
    const dueDate = new Date(dataPrazo)
    const diffTime = dueDate.getTime() - today.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    if (diffDays < 0) return 'high'
    if (diffDays <= 3) return 'high'
    if (diffDays <= 7) return 'medium'
    return 'low'
  }

  const responsaveis = parseResponsaveis(apiActivity.responsaveis)
  const isActivityOverdue = isOverdue(apiActivity.data_prazo, apiActivity.status)

  // Não calcular progresso aqui - será calculado recursivamente depois
  const internalStatus = mapStatusToInternal(apiActivity.status)

  return {
    id: apiActivity.id,
    order: apiActivity.ordem,
    title: apiActivity.titulo,
    description: apiActivity.descricao,
    status: internalStatus,
    statusId: apiActivity.status_id || getStatusIdByName(apiActivity.status, statusList),
    progress: null, // Será calculado em updateRecursiveProgress
    dueDate: apiActivity.data_prazo,
    createdAt: apiActivity.data_criacao,
    completedAt: apiActivity.data_finalizacao,
    authorId: apiActivity.autor_id,
    author: apiActivity.autor,
    assignees: responsaveis.map(r => r.name),
    responsaveis: responsaveis,
    hasAlert: isActivityOverdue,
    isOverdue: isActivityOverdue,
    priority: inferPriority(apiActivity.data_prazo),
    completedSubtasks: 0,
    totalSubtasks: 0,
    subtasks: [],
    updatedAt: apiActivity.data_criacao,
    children: [],
    level: 0,
    ordem: apiActivity.ordem
  }
}

const getStatusIdByName = (statusName, statusList) => {
  const statusItem = statusList.find(s => s.status === statusName)
  return statusItem ? statusItem.id : '1'
}

export function useActivities() {
  const state = reactive({
    activities: [],
    allActivities: [],
    hierarchyActivities: [],
    statusList: [],
    loading: false,
    error: null,
    filters: {
      status: null,
      search: '',
      dateRange: null,
      assignee: null
    },
    sorting: {
      field: 'ordem',
      order: 'asc'
    }
  })

  const mainActivities = computed(() => {
    return state.activities.filter(activity => {
      const level = calculateLevel(activity.ordem || activity.order)
      return level === 0
    })
  })

  const mainActivitiesWithHierarchy = computed(() => {
    if (state.hierarchyActivities.length > 0) {
      return state.hierarchyActivities
    }
    return mainActivities.value
  })

  // Função auxiliar para verificar se uma atividade corresponde aos filtros
  const activityMatchesFilters = (activity) => {
    // Filtro de busca
    if (state.filters.search) {
      const search = state.filters.search.toLowerCase()
      const matchesTitle = activity.title?.toLowerCase().includes(search)
      const matchesDescription = activity.description?.toLowerCase().includes(search)
      if (!matchesTitle && !matchesDescription) {
        return false
      }
    }

    // Filtro de status
    if (state.filters.status) {
      if (activity.status !== state.filters.status) {
        return false
      }
    }

    if (state.filters.assignee) {
      let hasAssignee = false

      // Verificar no array de assignees (strings)
      if (Array.isArray(activity.assignees) && activity.assignees.length > 0) {
        hasAssignee = activity.assignees.some(assignee => {
          if (typeof assignee === 'string') {
            return assignee === state.filters.assignee ||
              assignee.toLowerCase() === state.filters.assignee.toLowerCase()
          }
          return false
        })
      }

      // Verificar no array de responsaveis (objetos)
      if (!hasAssignee && Array.isArray(activity.responsaveis) && activity.responsaveis.length > 0) {
        hasAssignee = activity.responsaveis.some(resp => {
          return resp.name === state.filters.assignee ||
            resp.id === state.filters.assignee ||
            resp.nome === state.filters.assignee ||
            resp.name?.toLowerCase() === state.filters.assignee.toLowerCase() ||
            resp.nome?.toLowerCase() === state.filters.assignee.toLowerCase()
        })
      }

      if (!hasAssignee) {
        return false
      }
    }

    // Filtro de data/vencimento
    if (state.filters.dateRange) {

      if (!activity.dueDate) return false

      const today = DateTime.now().startOf('day')
      const dueDate = DateTime.fromISO(activity.dueDate).startOf('day')

      console.log({
        rawDueDate: activity.dueDate,
        dueDate: dueDate.toISODate(),
        today: today.toISODate(),
        status: activity.status
      })

      switch (state.filters.dateRange) {
        case 'today':
          if (!dueDate.equals(today)) return false
          break
        case 'this_week':
          const weekEnd = today.plus({ days: 7 })
          if (dueDate < today || dueDate > weekEnd) return false
          break
        case 'this_month':
          const monthStart = today.startOf('month')
          const monthEnd = today.endOf('month')
          if (dueDate < monthStart || dueDate > monthEnd) return false
          break
        case 'overdue':
          // VENCIDAS = data menor que hoje E não completada
          if (dueDate.toMillis() >= today.toMillis()) return false
          if (activity.status === 'completed') return false
          break
        case 'next_7_days':
          const next7 = today.plus({ days: 7 })
          if (dueDate < today || dueDate > next7) return false
          break
      }
    }

    return true
  }

  // Função recursiva para filtrar mantendo hierarquia inteligente
  const filterActivitiesRecursive = (activities) => {
    const filtered = []

    for (const activity of activities) {
      const activityMatches = activityMatchesFilters(activity)
      const hasChildren = activity.children && activity.children.length > 0

      if (hasChildren) {
        // SEMPRE filtrar os filhos recursivamente primeiro
        const filteredChildren = filterActivitiesRecursive(activity.children)

        if (activityMatches) {
          // Pai corresponde: incluir pai APENAS com filhos que também correspondem
          filtered.push({
            ...activity,
            children: filteredChildren, // Apenas filhos filtrados
            subtasks: filteredChildren  // Manter consistência
          })
        } else if (filteredChildren.length > 0) {
          // Pai NÃO corresponde mas tem filhos que correspondem:
          // incluir filhos separados (sem o pai)
          filtered.push(...filteredChildren)
        }
        // Se pai não corresponde E não tem filhos que correspondem: não incluir nada
      } else {
        // Sem filhos: incluir apenas se corresponder
        if (activityMatches) {
          filtered.push({ ...activity })
        }
      }
    }

    return filtered
  }

  const filteredActivities = computed(() => {
    // Se não houver filtros ativos, retornar hierarquia completa
    const hasActiveFilters = state.filters.search ||
      state.filters.status ||
      state.filters.assignee ||
      state.filters.dateRange

    if (!hasActiveFilters) {
      return state.hierarchyActivities
    }

    // Aplicar filtros com hierarquia inteligente
    return filterActivitiesRecursive(state.hierarchyActivities)
  })

  const activitiesByStatus = computed(() => {
    const grouped = {
      not_started: [],
      in_progress: [],
      completed: [],
      overdue: []
    }

    mainActivities.value.forEach(activity => {
      if (grouped[activity.status]) {
        grouped[activity.status].push(activity)
      }
    })

    return grouped
  })

  const activityStats = computed(() => {
    const activities = mainActivities.value
    const total = activities.length
    const completed = activities.filter(a => a.status === 'completed').length
    const inProgress = activities.filter(a => a.status === 'in_progress').length
    const notStarted = activities.filter(a => a.status === 'not_started').length
    const overdue = activities.filter(a => a.isOverdue && a.status !== 'completed').length

    return {
      total,
      completed,
      inProgress,
      notStarted,
      overdue,
      completionRate: total > 0 ? Math.round((completed / total) * 100) : 0
    }
  })

  const overdueActivities = computed(() => {
    return mainActivities.value.filter(activity =>
      activity.isOverdue && activity.status !== 'completed'
    )
  })

  const statusOptions = computed(() => {
    return state.statusList.map(status => ({
      value: mapStatusToInternal(status.status),
      title: status.status,
      id: status.id
    }))
  })

  const mapStatusToInternal = (apiStatus) => {
    const mapping = {
      'Não Iniciada': 'not_started',
      'Em Andamento': 'in_progress',
      'Concluída': 'completed'
    }
    return mapping[apiStatus] || 'not_started'
  }

  const fetchStatus = async () => {
    try {
      const response = await api.get('/atividades/status')
      if (response.data.status === 200 && response.data.data) {
        state.statusList = response.data.data
      }
    } catch (error) {
      console.error('Erro ao buscar status:', error)
      state.statusList = [
        { id: '1', status: 'Não Iniciada' },
        { id: '2', status: 'Em Andamento' },
        { id: '3', status: 'Concluída' }
      ]
    }
  }

  const updateRecursiveProgress = (activities) => {
    const getStatusProgress = (status) => {
      const statusProgressMap = {
        'not_started': 0,
        'in_progress': 50,
        'completed': 100
      }
      return statusProgressMap[status] || 0
    }

    const updateProgress = (activity) => {
      if (activity.children && activity.children.length > 0) {
        // Primeiro, atualizar recursivamente os filhos
        activity.children.forEach(updateProgress)

        // Calcular progresso como MÉDIA dos progressos dos filhos
        const totalChildren = activity.children.length
        const sumProgress = activity.children.reduce((sum, child) => {
          return sum + (child.progress || 0)
        }, 0)

        // Progresso = média aritmética dos progressos dos filhos
        // SEMPRE recalcular, ignorando valor anterior
        activity.progress = Math.round(sumProgress / totalChildren)
        activity.totalSubtasks = totalChildren
        activity.completedSubtasks = activity.children.filter(child => child.status === 'completed').length

        // O status permanece o que foi definido pelo usuário
        // Sincronizar subtasks para compatibilidade
        if (!activity.subtasks || activity.subtasks.length === 0) {
          activity.subtasks = activity.children
        }
      } else {
        // Sem filhos: progresso SEMPRE baseado no próprio status
        activity.totalSubtasks = 0
        activity.completedSubtasks = 0
        if (!activity.subtasks) {
          activity.subtasks = []
        }

        // SEMPRE recalcular baseado no status, ignorando qualquer valor anterior
        activity.progress = getStatusProgress(activity.status)
      }
    }

    activities.forEach(updateProgress)
  }

  const fetchActivities = async () => {
    state.loading = true
    state.error = null

    try {
      if (state.statusList.length === 0) {
        await fetchStatus()
      }

      const response = await api.get(`/atividades/${localStorage.getItem('rn573_user_id')}`)

      if (response.data.status === 200) {
        state.allActivities = response.data.data

        const mappedActivities = response.data.data.map(activity =>
          mapApiToActivity(activity, response.data.data, state.statusList)
        )

        state.activities = mappedActivities
        state.hierarchyActivities = buildHierarchyByOrder(mappedActivities)
        updateRecursiveProgress(state.hierarchyActivities)
      } else {
        throw new Error(response.data.message || 'Erro ao buscar atividades')
      }
    } catch (error) {
      console.error('Erro ao buscar atividades:', error)
    } finally {
      state.loading = false
    }
  }

  const createActivity = async (activityData) => {
    state.loading = true
    try {
      if (!activityData.ordem) {
        activityData.ordem = calculateNextOrder(state.activities)
      }

      const apiData = {
        ...activityData,
        criado_por: activityData.criado_por || localStorage.getItem('rn573_user_id'),
        data_finalizacao: activityData.data_finalizacao || null,
        data_exclusao: activityData.data_exclusao || null
      }

      const response = await api.post('/atividades', apiData)

      if (response.data.status === 200 || response.data.status === 201) {
        await fetchActivities()
        return response.data
      } else {
        throw new Error(response.data.message || 'Erro ao criar atividade')
      }
    } catch (error) {
      throw error
    } finally {
      state.loading = false
    }
  }

  const createSubtask = async (parentId, subtaskData) => {
    state.loading = true
    try {
      if (!subtaskData.ordem) {
        subtaskData.ordem = calculateNextOrder(state.activities, parentId)
      }

      const apiData = {
        ...subtaskData,
        criado_por: subtaskData.criado_por || localStorage.getItem('rn573_user_id'),
        data_finalizacao: subtaskData.data_finalizacao || null,
        data_exclusao: subtaskData.data_exclusao || null
      }

      const response = await api.post('/atividades', apiData)

      if (response.data.status === 200 || response.data.status === 201) {
        await fetchActivities()
        return response.data
      } else {
        throw new Error(response.data.message || 'Erro ao criar subtarefa')
      }
    } catch (error) {
      throw error
    } finally {
      state.loading = false
    }
  }

  const updateActivity = async (activityId, updates) => {
    state.loading = true
    try {
      const apiData = {
        ...updates,
        criado_por: updates.criado_por || localStorage.getItem('rn573_user_id'),
        data_finalizacao: updates.data_finalizacao || null,
        data_exclusao: updates.data_exclusao || null
      }

      const response = await api.put(`/atividades/${activityId}`, apiData)

      if (response.data.status === 200) {
        await fetchActivities()
        return response.data
      } else {
        throw new Error(response.data.message || 'Erro ao atualizar atividade')
      }
    } catch (error) {
      throw error
    } finally {
      state.loading = false
    }
  }

  const deleteActivity = async (activityId) => {
    state.loading = true
    try {
      const response = await api.delete(`/atividades/${activityId}`)

      if (response.data.status === 200) {
        await fetchActivities()
        return response.data
      } else {
        throw new Error(response.data.message || 'Erro ao excluir atividade')
      }
    } catch (error) {
      throw error
    } finally {
      state.loading = false
    }
  }

  const updateActivityStatus = async (activityId, newStatus) => {
    try {
      const currentActivity = state.activities.find(a => a.id === activityId)
      if (!currentActivity) {
        throw new Error('Atividade não encontrada')
      }

      const statusItem = statusOptions.value.find(s => s.value === newStatus)
      if (!statusItem) {
        throw new Error('Status não encontrado')
      }

      const updates = {
        titulo: currentActivity.title,
        descricao: currentActivity.description,
        status: statusItem.id,
        data_prazo: currentActivity.dueDate,
        data_finalizacao: newStatus === 'completed' ? new Date().toISOString().split('T')[0] : null,
        ordem: currentActivity.ordem || currentActivity.order
      }

      return await updateActivity(activityId, updates)
    } catch (error) {
      console.error('Erro ao atualizar status da atividade:', error)
      throw error
    }
  }

  const updateSubtaskStatus = async (subtaskId, newStatus) => {
    try {
      let currentSubtask = null
      for (const activity of state.activities) {
        if (activity.subtasks) {
          currentSubtask = activity.subtasks.find(sub => sub.id === subtaskId)
          if (currentSubtask) break
        }
      }

      if (!currentSubtask) {
        throw new Error('Subtarefa não encontrada')
      }

      const statusItem = statusOptions.value.find(s => s.value === newStatus)
      if (!statusItem) {
        throw new Error('Status não encontrado')
      }

      const updates = {
        titulo: currentSubtask.title,
        descricao: currentSubtask.description,
        status: statusItem.id,
        data_prazo: currentSubtask.dueDate,
        data_finalizacao: newStatus === 'completed' ? new Date().toISOString().split('T')[0] : null,
        ordem: currentSubtask.ordem || currentSubtask.order
      }

      return await updateActivity(subtaskId, updates)
    } catch (error) {
      console.error('Erro ao atualizar status da subtarefa:', error)
      throw error
    }
  }

  const updateActivityProgress = async (activityId, progress) => {
    const currentActivity = state.activities.find(a => a.id === activityId)
    if (!currentActivity) {
      throw new Error('Atividade não encontrada')
    }

    let newStatus = currentActivity.status
    if (progress === 0) {
      newStatus = 'not_started'
    } else if (progress === 100) {
      newStatus = 'completed'
    } else {
      newStatus = 'in_progress'
    }

    return updateActivityStatus(activityId, newStatus)
  }

  const setFilter = (filterType, value) => {
    state.filters[filterType] = value
  }

  const clearFilters = () => {
    state.filters = {
      status: null,
      search: '',
      dateRange: null,
      assignee: null
    }
  }

  const setSorting = (field, order = 'asc') => {
    state.sorting.field = field
    state.sorting.order = order
  }

  const getActivityById = (id) => {
    return state.activities.find(activity => activity.id === id)
  }

  const getSubtasksByParentId = (parentId) => {
    return state.activities.filter(activity => activity.parentId === parentId)
  }

  const getActivityWithHierarchy = (id) => {
    const findInHierarchy = (activities, targetId) => {
      for (const activity of activities) {
        if (activity.id == targetId) {
          return activity
        }
        if (activity.children && activity.children.length > 0) {
          const found = findInHierarchy(activity.children, targetId)
          if (found) return found
        }
      }
      return null
    }

    return findInHierarchy(state.hierarchyActivities, id)
  }

  const findActivityInHierarchy = (activities, id) => {
    for (const activity of activities) {
      if (activity.id == id) {
        return activity
      }
      if (activity.children && activity.children.length > 0) {
        const found = findActivityInHierarchy(activity.children, id)
        if (found) return found
      }
    }
    return null
  }

  const duplicateActivity = async (activityId) => {
    const original = getActivityById(activityId)
    if (!original) {
      throw new Error('Atividade não encontrada')
    }

    const duplicate = {
      titulo: `${original.title} (Cópia)`,
      descricao: original.description,
      status: '1',
      data_prazo: original.dueDate,
      responsaveis_ids: null,
      responsaveis: null
    }

    return createActivity(duplicate)
  }

  const getNextOrder = (parentId = null) => {
    return calculateNextOrder(state.activities, parentId)
  }

  return {
    state,
    mainActivities,
    mainActivitiesWithHierarchy,
    filteredActivities,
    activitiesByStatus,
    activityStats,
    overdueActivities,
    statusOptions,
    fetchActivities,
    fetchStatus,
    createActivity,
    createSubtask,
    updateActivity,
    deleteActivity,
    updateActivityStatus,
    updateSubtaskStatus,
    updateActivityProgress,
    setFilter,
    clearFilters,
    setSorting,
    getActivityById,
    getSubtasksByParentId,
    duplicateActivity,
    getActivityWithHierarchy,
    findActivityInHierarchy,
    getNextOrder
  }
}

export function useActivityFormatters() {
  const formatDate = (dateString) => {
    if (!dateString) return ''

    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    } catch {
      return dateString
    }
  }

  const formatDateRelative = (dateString) => {
    if (!dateString) return ''

    try {
      const date = new Date(dateString)
      const now = new Date()
      const diffTime = date.getTime() - now.getTime()
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

      if (diffDays === 0) return 'Hoje'
      if (diffDays === 1) return 'Amanhã'
      if (diffDays === -1) return 'Ontem'
      if (diffDays > 0) return `Em ${diffDays} dias`
      return `${Math.abs(diffDays)} dias atrás`
    } catch {
      return dateString
    }
  }

  const getStatusColor = (status) => {
    const colors = {
      'not_started': 'grey',
      'in_progress': 'warning',
      'completed': 'success',
      'overdue': 'error'
    }
    return colors[status] || 'grey'
  }

  const getStatusText = (status) => {
    const texts = {
      'not_started': 'Não Iniciada',
      'in_progress': 'Em Andamento',
      'completed': 'Concluída',
      'overdue': 'Atrasada'
    }
    return texts[status] || 'Desconhecido'
  }

  const getPriorityColor = (priority) => {
    const colors = {
      'low': 'success',
      'medium': 'warning',
      'high': 'error'
    }
    return colors[priority] || 'grey'
  }

  const getPriorityText = (priority) => {
    const texts = {
      'low': 'Baixa',
      'medium': 'Média',
      'high': 'Alta'
    }
    return texts[priority] || 'Não definida'
  }

  const formatProgress = (progress) => {
    return `${Math.round(progress)}%`
  }

  const getProgressColor = (progress) => {
    if (progress === 0) return 'grey'
    if (progress < 30) return 'error'
    if (progress < 70) return 'warning'
    return 'success'
  }

  return {
    formatDate,
    formatDateRelative,
    getStatusColor,
    getStatusText,
    getPriorityColor,
    getPriorityText,
    formatProgress,
    getProgressColor
  }
}
